import { useState, useCallback } from 'react';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import ToolSeoContent from 'components/ToolSeoContent';

export default function WordCounter() {
  const [text, setText] = useState('');
  const [copied, setCopied] = useState(false);

  const s = useCallback(() => {
    const w = text.trim() ? text.trim().split(/\s+/).length : 0;
    const c = text.length;
    const ns = text.replace(/\s/g,'').length;
    const se = text.trim() ? text.split(/[.!?]+/).filter(x=>x.trim()).length : 0;
    const pa = text.trim() ? text.split(/\n\s*\n/).filter(x=>x.trim()).length : 0;
    const rt = Math.max(1, Math.ceil(w/200));
    const uw = text.trim() ? new Set(text.toLowerCase().match(/\b\w+\b/g)||[]).size : 0;
    return { w, c, ns, se, pa, rt, uw };
  }, [text]);

  const stats = s();

  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(()=>setCopied(false),2000); };

  const STATS = [
    { icon:'type',      val: stats.w,              label:'Words' },
    { icon:'word',      val: stats.c,              label:'Characters' },
    { icon:'compress',  val: stats.ns,             label:'No Spaces' },
    { icon:'blog',      val: stats.se,             label:'Sentences' },
    { icon:'merge',     val: stats.pa,             label:'Paragraphs' },
    { icon:'star',      val: stats.uw,             label:'Unique Words' },
    { icon:'calendar',  val: `~${stats.rt} min`,  label:'Reading Time' },
  ];

  const keywords = text.trim()
    ? Object.entries((text.toLowerCase().match(/\b\w{4,}\b/g)||[]).reduce((a,w)=>{a[w]=(a[w]||0)+1;return a},{})).sort((a,b)=>b[1]-a[1]).slice(0,6)
    : [];

  return (
    <>
    <Seo title="Word Counter — Count Words & Characters" description="Count words, characters, sentences and paragraphs online. Free." path="/tools/text/word-counter" />
    <div className="tool-pg">
      <div className="container" style={{ maxWidth: 900 }}>
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon"><Icon name="type" size={24} /></div>
            <div><h1>Word Counter</h1><p>Real-time word, character, and sentence analysis. Just type or paste your text.</p></div>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {STATS.map(item => (
            <div key={item.label} className="stat-card">
              <div className="stat-icon"><Icon name={item.icon} size={18} /></div>
              <div className="stat-val">{item.val}</div>
              <div className="stat-lbl">{item.label}</div>
            </div>
          ))}
        </div>

        <div className="tool-box">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <label style={{ fontWeight:600, fontSize:14, color:'var(--slate-700)' }}>Your Text</label>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-secondary btn-sm" onClick={copy} disabled={!text}>
                <Icon name={copied?'check':'copy'} size={14} />{copied?'Copied!':'Copy'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={()=>setText('')} disabled={!text}>
                <Icon name="trash" size={14} />Clear
              </button>
            </div>
          </div>
          <textarea
            className="form-control"
            rows={12}
            placeholder="Start typing or paste your text here — stats update in real time..."
            value={text}
            onChange={e=>setText(e.target.value)}
            style={{ resize:'vertical', fontSize:15, lineHeight:1.75 }}
          />
        </div>

        {keywords.length > 0 && (
          <div className="alert alert-info" style={{ marginTop:16, display:'block' }}>
            <p style={{ fontWeight:700, fontSize:12, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:10 }}>Top Keywords (4+ letters)</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {keywords.map(([word, count]) => (
                <span key={word} style={{ background:'var(--white)', border:'1px solid var(--blue-200)', borderRadius:'var(--r-full)', padding:'4px 12px', fontSize:13, color:'var(--blue-dd)', fontWeight:500 }}>
                  {word} <strong>({count})</strong>
                </span>
              ))}
            </div>
          </div>
        )}
        <ToolSeoContent tool="word-counter" />
      </div>
    </div>
  </>
  );
}
